/**
 * Unified Missing Fish Intelligence Generator
 *
 * Single entry point for both Spreadsheet and Growth vs Model pages.
 * Produces one result_object that all UI views and PDF reports read from.
 *
 * Usage:
 *   const intel = generateMissingFishIntelligence({ daily_analysis_rows, activeRecord, economicParams });
 *   const intel = generateMissingFishIntelligence({ growthModelResult });
 */

import { calculateMissingFishIntelligence } from '@/utils/calculateMissingFishIntelligence';
import {
  runMissingFishDetection,
  calculateEconomicImpact,
  calculateDataQualityScore,
  checkMortalityReality,
  getSamplingRecommendation,
  detectOverfeeding,
  getGraphInsight,
  detectConfirmedSampleJump,
  detectFCRBasedMissingFish,
  findBestFitFishNumber,
} from '@/utils/missingFishDetection';
import { resolveFishCount, detectSampleAnomalies, applyAnomalyPenalty } from '@/utils/calculationEngine';
import { resolveDecisionState } from '@/utils/decisionState';
import { detectFCROutliers } from '@/utils/fcrOutliers';
import {
  computeMissingFishRange,
  formatMissingFishRange,
  toCanonicalConfidenceLabel,
  CONFIDENCE_LABELS,
} from '@/utils/reportFormatters';

// ---------------------------------------------------------------------------
// Helper: build comparisons from a Spreadsheet activeRecord
// ---------------------------------------------------------------------------
function buildComparisonsFromRecord(activeRecord) {
  if (!activeRecord?.sample_points?.length) return [];

  return activeRecord.sample_points.map((sample, index) => {
    const sampleDate = sample.date;
    const actualWeight = parseFloat(sample.weight);

    let modelWeight = actualWeight;
    let daysSinceStart = 0;

    if (activeRecord.model_trajectory?.length > 0) {
      const modelPoint = activeRecord.model_trajectory.find(m => m.date === sampleDate);
      if (modelPoint) {
        modelWeight = parseFloat(modelPoint.weight);
      } else {
        const sampleTime = new Date(sampleDate).getTime();
        let closestPoint = activeRecord.model_trajectory[0];
        let closestDiff = Math.abs(new Date(closestPoint.date).getTime() - sampleTime);

        for (const mt of activeRecord.model_trajectory) {
          const diff = Math.abs(new Date(mt.date).getTime() - sampleTime);
          if (diff < closestDiff) {
            closestDiff = diff;
            closestPoint = mt;
          }
        }
        modelWeight = parseFloat(closestPoint.weight);
      }
    }

    if (activeRecord.start_date) {
      const startDate = new Date(activeRecord.start_date);
      const sampleDateObj = new Date(sampleDate);
      daysSinceStart = Math.round((sampleDateObj - startDate) / (1000 * 60 * 60 * 24));
    }

    return {
      sample_number: index + 1,
      sample_date: sampleDate,
      days_since_arrival: daysSinceStart,
      actual_weight_g: actualWeight,
      model_weight_g: modelWeight,
    };
  });
}

// ---------------------------------------------------------------------------
// Decision Context Builder — Structured decision support with risk control
// ---------------------------------------------------------------------------

/**
 * Builds the DecisionContext wrapper from existing SSOT data.
 * NO new calculations — this is a response formatter only.
 * 
 * GLOBAL POLICY: Results inform planning, feeding, and operational review.
 * Results must NEVER directly overwrite official stock records.
 */
function buildDecisionContext({
  ssot,
  dataQuality,
  sharedMetrics,
  detectionConfidence,
  methodResolution,
  fcrBasedDetection,
  trajectoryJump,
  economicParams,
  rfi,
  wastedFeedKg,
  feedingState,
  isSpreadsheetMode,
  isGrowthModelMode,
  unifiedDecision = null,  // ★ Pre-resolved decision from resolveDecisionState() — injected by caller
}) {
  // ══════════════════════════════════════════════════════════════════════════
  // 1. DETECTION
  // ══════════════════════════════════════════════════════════════════════════
  const hasMissing = ssot?.hasMissingFish;
  const missingCount = ssot?.missingFishCount || 0;
  const missingPercent = ssot?.missingFishPercent || 0;
  const strength = ssot?.detectionStrength || 'Low';
  
  // Determine detection type from existing signals (internal classification only)
  // ★ NOTE: This is for internal logic only — user-facing labels come from SSOT override
  let detectionType = 'Normal';
  if (hasMissing && missingCount > 0) {
    detectionType = 'Missing Fish';
  } else if (sharedMetrics?.growth_direction === 'below' && !hasMissing) {
    detectionType = 'Below Model'; // Changed from 'Growth Suppression' — less presumptive
  } else if (missingCount < 0) {
    detectionType = 'Extra Fish';
  }
  
  // Probability estimate based on detection strength
  const probabilityMap = { 'High': 85, 'Moderate': 65, 'Low': 35 };
  const probability = hasMissing ? (probabilityMap[strength] || 50) : null;

  const detection = {
    type: detectionType,
    strength: strength,
    probability,
    summary: null, // ★ PURGED — narrative from ssotOverride.js only
  };

  // ══════════════════════════════════════════════════════════════════════════
  // 2. DATA RELIABILITY (with interpretable drivers)
  // ══════════════════════════════════════════════════════════════════════════
  const reliabilityScore = dataQuality?.score ?? 50;
  const subscores = dataQuality?.subscores || {};
  const details = dataQuality?.details || {};
  
  // Build reliability drivers (user-facing explanations)
  const reliabilityDrivers = [];
  
  // Sample count
  const sampleCount = details.totalSamples || details.sampleCount || 0;
  if (sampleCount >= 10) {
    reliabilityDrivers.push({ factor: 'Sample count', status: 'good', detail: `${sampleCount} samples` });
  } else if (sampleCount >= 5) {
    reliabilityDrivers.push({ factor: 'Sample count', status: 'moderate', detail: `${sampleCount} samples (borderline)` });
  } else if (sampleCount > 0) {
    reliabilityDrivers.push({ factor: 'Sample count', status: 'low', detail: `Only ${sampleCount} sample${sampleCount === 1 ? '' : 's'}` });
  }
  
  // Sample recency
  const avgInterval = details.avgSampleInterval;
  if (avgInterval && avgInterval <= 7) {
    reliabilityDrivers.push({ factor: 'Sample recency', status: 'good', detail: 'Weekly or better' });
  } else if (avgInterval && avgInterval <= 14) {
    reliabilityDrivers.push({ factor: 'Sample recency', status: 'moderate', detail: `~${Math.round(avgInterval)}d intervals` });
  } else if (avgInterval && avgInterval > 14) {
    reliabilityDrivers.push({ factor: 'Sample recency', status: 'low', detail: `${Math.round(avgInterval)}d gaps — sparse` });
  }
  
  // Signal agreement/conflict
  const hasConflictingSignals = !!(
    (methodResolution?.jumpAvailable && methodResolution?.fcrAvailable) &&
    (methodResolution?.jumpFishNumber !== methodResolution?.fcrFishNumber)
  );
  if (methodResolution?.jumpAvailable && methodResolution?.fcrAvailable) {
    if (hasConflictingSignals) {
      reliabilityDrivers.push({ factor: 'Signal agreement', status: 'low', detail: 'FCR vs Jump conflict' });
    } else {
      reliabilityDrivers.push({ factor: 'Signal agreement', status: 'good', detail: 'Methods align' });
    }
  }
  
  // Feed data availability
  const feedCostPerKg = parseFloat(economicParams?.average_feed_price_eur_per_kg) || 0;
  const hasFeedData = feedCostPerKg > 0 && (wastedFeedKg != null || rfi != null);
  if (hasFeedData) {
    reliabilityDrivers.push({ factor: 'Feed data', status: 'good', detail: 'Available' });
  } else {
    reliabilityDrivers.push({ factor: 'Feed data', status: 'low', detail: 'Missing — no FCR certainty' });
  }
  
  // Sample volatility (from FCR or weight variance)
  const fcrVolatility = fcrBasedDetection?.fcrVolatility || details.fcrVolatility;
  const hasSuspectedSampleError = details.suspectedSampleError || (fcrVolatility && fcrVolatility > 0.3);
  const highVolatility = fcrVolatility && fcrVolatility > 0.25;
  if (hasSuspectedSampleError) {
    reliabilityDrivers.push({ factor: 'Sample quality', status: 'low', detail: 'Suspected error detected' });
  } else if (highVolatility) {
    reliabilityDrivers.push({ factor: 'Sample volatility', status: 'moderate', detail: 'High variance in measurements' });
  }
  
  // Build flags (for quick visual indicators)
  const flags = [];
  if (subscores.sampleFrequency < 50) flags.push('low_samples');
  if (subscores.feedContinuity < 50) flags.push('missing_feed');
  if (subscores.temperatureData < 50) flags.push('temp_gaps');
  if (subscores.mortalityReporting < 50) flags.push('mortality_gaps');
  if (hasConflictingSignals) flags.push('signal_conflict');
  if (hasSuspectedSampleError) flags.push('sample_error');
  if (highVolatility) flags.push('high_volatility');
  
  // Sensitivity note
  const sensitivityNote = avgInterval && avgInterval > 21
    ? `High sensitivity to sample accuracy — ±5% error changes conclusion.`
    : avgInterval && avgInterval > 14
      ? `Moderate sensitivity. Resample within 14 days.`
      : hasSuspectedSampleError
        ? `Sample error flagged. Validate before acting.`
        : null;

  const dataReliability = {
    score: reliabilityScore,
    label: reliabilityScore >= 70 ? 'Good' : reliabilityScore >= 50 ? 'Acceptable' : 'Low',
    drivers: reliabilityDrivers,
    flags,
    sensitivityNote,
    warning: reliabilityScore < 50 ? 'Low data reliability — interpret with caution.' : null,
    hasSuspectedSampleError,
    highVolatility,
  };

  // ══════════════════════════════════════════════════════════════════════════
  // 3. FINANCIAL CONTEXT (uncertainty-aware)
  // ══════════════════════════════════════════════════════════════════════════
  // Financial figures are INDICATIVE when reliability is not high
  // SAFEGUARD: If reliability < 60 OR downgrades exist, mark as "Not decision-grade"
  const isHighReliability = reliabilityScore >= 70 && !hasConflictingSignals && !hasSuspectedSampleError;
  const isDecisionGrade = reliabilityScore >= 60 && !hasConflictingSignals && !hasSuspectedSampleError && !highVolatility;
  
  let financialContext = null;
  if (hasFeedData) {
    const cumulativeImpact = wastedFeedKg > 0 ? Math.round(wastedFeedKg * feedCostPerKg * 100) / 100 : 0;
    const dailyImpact = cumulativeImpact > 0 && details.feedCoveragePercent
      ? Math.round((cumulativeImpact / (details.feedCoveragePercent || 100)) * 100) / 100
      : 0;
    
    // RULE: If feeding < 100% → NO feed waste
    const isUnderfeeding = rfi != null && rfi < 100;
    
    // Uncertainty qualifier based on reliability
    const uncertaintyLevel = isHighReliability ? 'confirmed' : reliabilityScore >= 50 ? 'estimated' : 'indicative';
    const uncertaintyLabel = {
      confirmed: '',
      estimated: 'Estimated ',
      indicative: 'Indicative ',
    }[uncertaintyLevel];
    
    financialContext = {
      hasFeedData: true,
      isDecisionGrade,
      notDecisionGradeWarning: !isDecisionGrade && cumulativeImpact > 0 
        ? 'Not decision-grade — validation required' 
        : null,
      uncertaintyLevel,
      uncertaintyLabel,
      dailyImpactEur: isUnderfeeding ? 0 : dailyImpact,
      cumulativeImpactEur: isUnderfeeding ? 0 : cumulativeImpact,
      feedWasteRule: isUnderfeeding
        ? 'Growth suppression risk — not feed waste. RFI < 100%.'
        : cumulativeImpact > 0
          ? `${uncertaintyLabel}feed waste: ${wastedFeedKg?.toLocaleString()} kg across overfeeding periods.`
          : 'No feed waste detected.',
      caveat: !isHighReliability && cumulativeImpact > 0
        ? 'Planning estimate pending validation.'
        : null,
    };
  } else {
    financialContext = {
      hasFeedData: false,
      uncertaintyLevel: 'unavailable',
      uncertaintyLabel: '',
      dailyImpactEur: null,
      cumulativeImpactEur: null,
      feedWasteRule: 'No feed data — FCR-based financial certainty unavailable.',
      caveat: null,
    };
  }

  // ══════════════════════════════════════════════════════════════════════════
  // 4. DECISION CLASS — Standardized 3-state system (SINGLE SOURCE OF TRUTH)
  // ══════════════════════════════════════════════════════════════════════════
  // ★ The caller injects a pre-resolved decision produced by resolveDecisionState()
  //   in /app/frontend/src/utils/decisionState.js. This wrapper NEVER re-derives
  //   the class — that would create a second classifier and allow drift.
  //   Fallback: if no unifiedDecision is provided (legacy callers), resolve it here
  //   using the same single function. Never produce a custom rule.
  const resolved = unifiedDecision || resolveDecisionState({
    reliabilityScore,
    detectionStrength: detection.strength,
    hasConflict: hasConflictingSignals,
    hasSuspectedError: hasSuspectedSampleError,
    highVolatility,
    hasFeedData,
    sampleCount,
    isSnapshot: !isSpreadsheetMode && !isGrowthModelMode,
    hasMissingFish: hasMissing,
  });
  const decisionClass = resolved.decisionClass;
  const decisionReason = resolved.reason;
  const downgradeReasons = resolved.downgradeReasons;

  const hasDowngrades = downgradeReasons.length > 0;
  const severeDowngrade = reliabilityScore < 50 || hasSuspectedSampleError;
  const moderateDowngrade = hasDowngrades && !severeDowngrade;

  // ══════════════════════════════════════════════════════════════════════════
  // 5. ACTIONS (with PRIMARY ACTION prioritized)
  // ══════════════════════════════════════════════════════════════════════════
  let primaryAction = null;
  const secondaryActions = [];
  
  // Determine PRIMARY ACTION based on decision class and detection
  if (decisionClass === 'VALIDATION_REQUIRED') {
    primaryAction = {
      text: 'Re-sample and verify data before any operational change.',
      reversibility: 'REVERSIBLE',
      priority: 'PRIMARY'
    };
  } else if (decisionClass === 'ACTIONABLE' && detection.type === 'Missing Fish') {
    primaryAction = {
      text: 'Review biomass estimates in feeding calculations.',
      reversibility: 'REVERSIBLE',
      priority: 'PRIMARY'
    };
  } else if (detection.type === 'Growth Suppression') {
    primaryAction = {
      text: 'Review environmental conditions (O₂, density, temperature).',
      reversibility: 'REVERSIBLE',
      priority: 'PRIMARY'
    };
  } else {
    primaryAction = {
      text: 'Continue routine monitoring.',
      reversibility: 'REVERSIBLE',
      priority: 'PRIMARY'
    };
  }
  
  // Build secondary actions
  if (detection.type === 'Missing Fish' && detection.strength !== 'Low') {
    if (decisionClass === 'ACTIONABLE' && reliabilityScore < 80) {
      secondaryActions.push({ 
        text: 'Schedule confirmation sample within 14 days.', 
        reversibility: 'REVERSIBLE' 
      });
    }
  }
  if (detection.type === 'Growth Suppression' && rfi != null && rfi < 85) {
    secondaryActions.push({ 
      text: 'Consider increasing feed allocation 3–5%.', 
      reversibility: 'REVERSIBLE' 
    });
  }
  if (hasDowngrades && decisionClass !== 'VALIDATION_REQUIRED') {
    secondaryActions.push({ 
      text: 'Address data quality issues for improved confidence.', 
      reversibility: 'REVERSIBLE' 
    });
  }

  // ══════════════════════════════════════════════════════════════════════════
  // 6. RELIABILITY CLASSIFICATION
  // ══════════════════════════════════════════════════════════════════════════
  // Map numerical score to interpretable category
  let reliabilityClassification;
  if (reliabilityScore >= 80) {
    reliabilityClassification = { label: 'Strong dataset', tier: 'strong', color: 'green' };
  } else if (reliabilityScore >= 60) {
    reliabilityClassification = { label: 'Usable with caution', tier: 'moderate', color: 'amber' };
  } else if (reliabilityScore >= 40) {
    reliabilityClassification = { label: 'Limited dataset', tier: 'limited', color: 'orange' };
  } else {
    reliabilityClassification = { label: 'Insufficient evidence', tier: 'insufficient', color: 'red' };
  }
  
  // Attach classification to dataReliability
  dataReliability.classification = reliabilityClassification;

  // ══════════════════════════════════════════════════════════════════════════
  // 7. STOCK RECORD POLICY (GLOBAL — always present)
  // ══════════════════════════════════════════════════════════════════════════
  const stockRecordPolicy = {
    canModifyStockRecords: false, // NEVER true from analytical results alone
    message: 'Do NOT adjust official stock records based on analytical results alone.',
    detail: 'Results inform planning, feeding, and biomass interpretation. Stock record changes require physical validation (harvest count, camera inspection, or mortality audit).',
  };

  // ══════════════════════════════════════════════════════════════════════════
  // 8. VALIDATION REQUIREMENTS
  // ══════════════════════════════════════════════════════════════════════════
  // Trigger if: 
  // - Detection ≥ Moderate AND reliability < 70
  // - OR any downgrade gate active
  // - OR decision class is VALIDATION_REQUIRED
  const needsValidation = (
    (detection.strength !== 'Low' && reliabilityScore < 70) ||
    hasDowngrades ||
    decisionClass === 'VALIDATION_REQUIRED'
  );

  const validation = needsValidation ? {
    required: true,
    triggers: [
      reliabilityScore < 70 ? 'Data reliability < 70' : null,
      hasSuspectedSampleError ? 'Suspected sample error' : null,
      highVolatility ? 'High sample volatility' : null,
      sampleCount > 0 && sampleCount < 5 ? 'Low evidence depth' : null,
      detection.strength === 'Moderate' ? 'Moderate detection strength' : null,
    ].filter(Boolean),
    examples: [
      'Second sample weight measurement',
      'Camera/underwater inspection',
      'Harvest count reconciliation',
    ],
  } : {
    required: false,
    triggers: [],
    examples: [],
  };

  // ══════════════════════════════════════════════════════════════════════════
  // 9. DOWNGRADE EXPLANATION (user-facing)
  // ══════════════════════════════════════════════════════════════════════════
  const downgradeExplanation = downgradeReasons.length > 0
    ? `Reduced confidence due to: ${downgradeReasons.join(', ').toLowerCase()}`
    : null;

  return {
    detection,
    dataReliability,
    financialContext,
    decisionClass,
    decisionReason,
    downgradeReasons,
    downgradeExplanation,
    primaryAction,
    secondaryActions: secondaryActions.slice(0, 2), // Max 2 secondary
    stockRecordPolicy,
    validation,
    timestamp: new Date().toISOString(),
  };
}

// ---------------------------------------------------------------------------
// Main unified intelligence generator
// ---------------------------------------------------------------------------

/**
 * @param {Object}  params
 * @param {Array}   [params.daily_analysis_rows] - Foundation dataset (Spreadsheet mode)
 * @param {Object}  [params.activeRecord]        - Spreadsheet record
 * @param {Object}  [params.growthModelResult]    - Growth vs Model result
 * @param {Object}  [params.economicParams]       - { average_feed_price_eur_per_kg, fish_price_eur_per_kg, target_harvest_weight_kg }
 * @param {number}  [params.weightBasedModelFCR]  - Model FCR at last sample weight from lookup (same as Snapshot Analysis)
 * @param {number}  [params.reportedClosingFCR]   - Pre-computed reported closing FCR from shared calcClosingFCR engine
 * @param {Object}  [params.closingBiomassData]   - Pre-computed biomass components from shared engine
 * @returns {Object|null} Unified intelligence result
 */
export const generateMissingFishIntelligence = ({
  daily_analysis_rows = null,
  activeRecord = null,
  growthModelResult = null,
  economicParams = {},
  weightBasedModelFCR = null,
  reportedClosingFCR = null,
  closingBiomassData = null,
  detectionMethod = 'fcr',  // 'jump' | 'fcr'
  fcrPanelMissingFish = null, // from Closing Biological FCR panel's algebraic solution
  excludedIndices = new Set(), // User-controlled sample exclusion indices
  modelScalingFactor = 1, // Global best-fit scaling factor for adjusted model baseline
  bypassSpeciesClamp = false, // When true (Guard Rails OFF), skip Tilapia confidence clamp
}) => {
  const isSpreadsheetMode = !!(daily_analysis_rows?.length && activeRecord);
  const isGrowthModelMode = !isSpreadsheetMode && !!growthModelResult?.comparisons?.length;

  if (!isSpreadsheetMode && !isGrowthModelMode) return null;

  // ── 1. Build comparisons — enforce strict chronological order ────────
  const rawComparisons = isSpreadsheetMode
    ? buildComparisonsFromRecord(activeRecord)
    : growthModelResult.comparisons;

  if (!rawComparisons?.length) return null;

  // Sort by days_since_arrival to guarantee date-ordered analysis
  const comparisons = [...rawComparisons].sort(
    (a, b) => (a.days_since_arrival ?? 0) - (b.days_since_arrival ?? 0)
  );

  // ── 2. Core deviation analysis (shared engine) ────────────────────────
  // calculateMissingFishIntelligence produces: deviations, trend, confidence,
  // explanations, earliestActionable, feedWaste, stats, analyticalSignals
  // Pass modelScalingFactor so deviations use the SAME active baseline as the chart and jump detection
  const dailyFeedData = isGrowthModelMode ? (growthModelResult.daily_feed_data || null) : null;
  const activeScaling = isGrowthModelMode ? modelScalingFactor : 1;
  // Pass species through so calculateMissingFishIntelligence can apply the
  // Tilapia static-model confidence downgrade (safeguard §5 — one-step clamp).
  const speciesForClamp = isSpreadsheetMode
    ? (activeRecord?.species || null)
    : (growthModelResult?.species || null);
  const coreIntelligence = calculateMissingFishIntelligence(comparisons, dailyFeedData, excludedIndices, activeScaling, speciesForClamp, bypassSpeciesClamp);
  if (!coreIntelligence) return null;

  // ── 3. Biomass reconciliation detection (Spreadsheet mode) ────────────
  let detectionResults = null;
  if (isSpreadsheetMode) {
    // Note: runMissingFishDetection uses its own internal FCR lookup (getExpectedFCR)
    // for biomass reconciliation. The weightBasedModelFCR is used separately for
    // FCR-based detection in section 6b.
    detectionResults = runMissingFishDetection(daily_analysis_rows, activeRecord, null);
    coreIntelligence.missingFishDetection = detectionResults;
  }

  // ── 3b. Extreme-FCR outlier detection ─────────────────────────────────
  // ★ Guards against silent propagation of implausible per-period FCR values
  //   (typically data-entry errors like duplicated feed, swapped weights, or
  //   unreported mortality events) into cumulative missing-fish estimates.
  //   Implausible rows are excluded from FCR-derived aggregates and force the
  //   suspected-error flag that downgrades decision state via the SSOT.
  //   Rules live in /app/frontend/src/utils/fcrOutliers.js.
  const fcrOutliers = detectFCROutliers(detectionResults?.reconciliationRows || []);
  if (coreIntelligence) coreIntelligence.fcrOutliers = fcrOutliers;

  // ── 4. Overfeeding detection (kept for signal scoring, NOT for interpretation) ──
  // NOTE: RFI calculation moved to section 6d after initialAssumedFishNumber is resolved
  const overfeedingRisk = isSpreadsheetMode
    ? detectOverfeeding(detectionResults, coreIntelligence)
    : { level: 'Low', note: null, signals: { belowModel: 0, feedExceedsActual: 0, poorFCR: 0, persistent: false, totalPoints: 0 } };

  // ── 5. Trajectory jump detection (works for both modes) ───────────────
  const trajectoryJump = detectConfirmedSampleJump(
    detectionResults,
    coreIntelligence,
    overfeedingRisk,
    isGrowthModelMode ? growthModelResult : null,
    isGrowthModelMode ? modelScalingFactor : 1,
    excludedIndices,
  );

  // ── 5b. Post-jump-validation trend refinement ─────────────────────────
  const jumpSuspectIndices = new Set();  // Track suspect sample indices from jump analysis
  if (trajectoryJump?.suspect_samples?.length > 0) {
    trajectoryJump.suspect_samples.forEach(ss => {
      if (ss.sampleIndex != null) jumpSuspectIndices.add(ss.sampleIndex);
    });

    const baseTrend = coreIntelligence.trend.classification;
    const stats = coreIntelligence.stats;
    let directionLabel;
    if (stats.belowCount > stats.aboveCount) {
      directionLabel = 'Underperformance';
    } else if (stats.aboveCount > stats.belowCount) {
      directionLabel = 'Above-model trend';
    } else {
      directionLabel = 'Mixed pattern';
    }

    if (!baseTrend.includes('unconfirmed jump')) {
      coreIntelligence.trend.classification = `${directionLabel} with unconfirmed jump (likely sampling error)`;
      coreIntelligence.trend.class = 'bg-amber-50 text-amber-800 border-amber-300';
      // ★ PURGED: explanations generation removed — narrative from ssotOverride.js only
      coreIntelligence.explanations = [];
    }
  }
  // Attach jump-validated suspect indices for downstream use
  coreIntelligence.jumpValidatedSuspects = jumpSuspectIndices;

  // ── 6. Best-fit fish number via growth curve fitting ────────────────
  const originalFishNumber = isSpreadsheetMode
    ? (detectionResults?.inputs?.inputNumber || activeRecord.input_number)
    : growthModelResult?.starting_fish_number;

  const startingWeightG = isSpreadsheetMode
    ? activeRecord.input_average_weight
    : growthModelResult?.starting_weight;

  const growthFit = findBestFitFishNumber({
    reportedFishCount: originalFishNumber,
    startingWeightG,
    comparisons,
  });

  // ── 6b. FCR-based missing fish detection ──────────────────────────────
  let fcrBasedDetection = null;
  if (isSpreadsheetMode && daily_analysis_rows.length > 0 && activeRecord) {
    const modelFcrValue = weightBasedModelFCR;

    // Only run FCR-based detection when we have the proper model FCR
    // AND the pre-computed reported closing FCR from the shared engine
    if (modelFcrValue && reportedClosingFCR && closingBiomassData) {
      fcrBasedDetection = detectFCRBasedMissingFish({
        reportedClosingFCR,
        modelFCR: modelFcrValue,
        totalFeedKg: closingBiomassData.totalFeedKg,
        startBiomassKg: closingBiomassData.startBiomassKg,
        closingBiomassKg: closingBiomassData.closingBiomassKg,
        harvestBiomassKg: closingBiomassData.harvestBiomassKg,
        mortalityBiomassKg: closingBiomassData.mortalityBiomassKg,
        reportedRemaining: closingBiomassData.reportedRemaining,
        lastWeightG: closingBiomassData.lastWeightG,
        trajectoryJump,
      });
    }
  } else if (isGrowthModelMode && growthModelResult) {
    // Growth Model mode: compute biomass components + closing FCR locally
    const fishN = growthModelResult.starting_fish_number;
    const startW = growthModelResult.starting_weight;
    const lastComp = growthModelResult.comparisons?.[growthModelResult.comparisons.length - 1];
    const currW = lastComp?.actual_weight_g;
    let totalFeed = 0;
    if (growthModelResult.daily_feed_data?.length) {
      totalFeed = growthModelResult.daily_feed_data.reduce((s, d) => s + (d.feed_kg || 0), 0);
    }
    if (weightBasedModelFCR && fishN && startW && currW && totalFeed > 0 && currW > startW) {
      const startBio = (fishN * startW) / 1000;
      const closingBio = (fishN * currW) / 1000;
      const biomassGain = closingBio - startBio;
      const closingFCR = biomassGain > 0 ? totalFeed / biomassGain : null;
      if (closingFCR) {
        fcrBasedDetection = detectFCRBasedMissingFish({
          reportedClosingFCR: closingFCR,
          modelFCR: weightBasedModelFCR,
          totalFeedKg: totalFeed,
          startBiomassKg: startBio,
          closingBiomassKg: closingBio,
          reportedRemaining: fishN,
          lastWeightG: currW,
          trajectoryJump,
        });
      }
    }
  }

  // ── 6c. Resolve assumed fish number from selected detection method ────
  // A confirmed sample jump with a best-fit fish number estimate is a valid
  // missing-fish signal. The jump is always above trajectory (gate requirement).
  // Whether it's also above the growth model is secondary metadata.
  const jumpIsAboveModel = !!trajectoryJump?.jump_above_model;
  const jumpIsAboveTrajectory = !!trajectoryJump?.jump_above_trajectory;
  const jumpHasSignal = !!(
    trajectoryJump?.jump_event_confirmed &&
    (trajectoryJump.best_fit_fish_number || trajectoryJump.adjusted_fish_count)
  );
  const jumpFishNumber = jumpHasSignal
    ? (trajectoryJump.best_fit_fish_number || trajectoryJump.adjusted_fish_count)
    : null;

  // FCR fish number: prefer the Closing Biological FCR panel's algebraic solution
  // (fcrPanelMissingFish) over the intelligence engine's separate detection
  // to guarantee the automatic number matches "Strong Missing Fish Evidence" exactly.
  const panelFcrHasSignal = !!(fcrPanelMissingFish?.detected && fcrPanelMissingFish.adjustedN);
  const engineFcrHasSignal = !!(fcrBasedDetection?.detected && fcrBasedDetection.adjustedFishCount);
  const fcrHasSignal = panelFcrHasSignal || engineFcrHasSignal;
  const fcrFishNumber = panelFcrHasSignal
    ? fcrPanelMissingFish.adjustedN
    : (engineFcrHasSignal ? fcrBasedDetection.adjustedFishCount : null);

  let initialAssumedFishNumber = originalFishNumber || null;
  let activeDetectionMethod = detectionMethod;
  let fallbackOccurred = false;
  let fallbackReason = null;

  if (detectionMethod === 'jump') {
    if (jumpFishNumber) {
      initialAssumedFishNumber = jumpFishNumber;
      activeDetectionMethod = 'jump';
    } else if (fcrFishNumber) {
      initialAssumedFishNumber = fcrFishNumber;
      activeDetectionMethod = 'fcr';
      fallbackOccurred = true;
      fallbackReason = 'No confirmed jump signal — fell back to FCR';
    } else {
      // No signal from either method — stay on jump as active
      activeDetectionMethod = 'jump';
    }
  } else {
    if (fcrFishNumber) {
      initialAssumedFishNumber = fcrFishNumber;
      activeDetectionMethod = 'fcr';
    }
  }

  const methodResolution = {
    selectedMethod: detectionMethod,
    activeMethod: activeDetectionMethod,
    fallbackOccurred,
    fallbackReason,
    jumpAvailable: jumpHasSignal,
    jumpAboveModel: jumpIsAboveModel,
    jumpAboveTrajectory: jumpIsAboveTrajectory,
    jumpDetectedButBelowModel: !!(trajectoryJump?.jump_event_confirmed && !jumpIsAboveModel),
    fcrAvailable: fcrHasSignal,
    jumpFishNumber,
    fcrFishNumber,
  };

  // ── 6d. RFI (Relative Feed Index) & Feeding Impact Classification ─────
  //    RFI = (Actual Feed / Corrected Recommended Feed) × 100
  //    When an adjusted fish count exists, recommended feed is recomputed
  //    using the CORRECTED (adjusted) biomass — not the reported count.
  //    NOTE: This section MUST come after initialAssumedFishNumber is resolved.
  //
  //    ★ PERIOD-BY-PERIOD WASTED FEED CALCULATION ★
  //    totalWastedFeedKg = sum(max(0, actual_feed - corrected_recommended_feed))
  //    This ensures that overfeeding periods are captured accurately and aren't
  //    mathematically canceled out by later underfeeding periods.
  //    Example: If you overfeed 100kg in weeks 1-5 and underfeed 80kg in weeks 6-10,
  //    the cumulative approach would show only 20kg excess, but the true waste is
  //    100kg (you can't "reclaim" feed already given).
  let rfi = null;
  let feedingState = null;
  let totalActualFeedKg = 0;
  let totalCorrectedRecommendedKg = 0;
  let totalWastedFeedKg = 0;  // ★ Period-by-period wasted feed accumulator

  if (daily_analysis_rows && daily_analysis_rows.length > 0) {
    // ★ RFI uses only days the operator actually fed the cage. This keeps the
    // numerator (actual feed) and denominator (recommended feed) symmetric so
    // intentional no-feed days (sampling, fasting, severe weather, harvest-prep)
    // don't dilute the ratio and falsely flag a well-managed cage as underfed.
    const feedDays = daily_analysis_rows.filter(r => (r.daily_feed_kg || 0) > 0);
    totalActualFeedKg = feedDays.reduce((sum, r) => sum + (r.daily_feed_kg || 0), 0);

    // If we have an adjusted fish count that differs from reported, recompute recommended
    // feed using the CORRECTED biomass (adjusted count minus cumulative mortality).
    const useAdjusted = initialAssumedFishNumber && initialAssumedFishNumber !== originalFishNumber;

    if (useAdjusted) {
      let cumulativeMortality = 0;
      for (const row of daily_analysis_rows) {
        // Mortality and harvest still accrue every day regardless of feeding —
        // fish die / are harvested even on fasting days.
        cumulativeMortality += row.daily_mortality || 0;
        let correctedFishCount = Math.max(0, initialAssumedFishNumber - cumulativeMortality);
        if (row.harvest_number > 0) correctedFishCount = Math.max(0, correctedFishCount - row.harvest_number);

        // Recommended-feed and waste accumulate ONLY on days the cage was fed.
        const wasFed = (row.daily_feed_kg || 0) > 0;
        if (wasFed && row.actual_weight_g != null && correctedFishCount > 0 && row.sfr_percent != null) {
          const correctedBiomass = (row.actual_weight_g * correctedFishCount) / 1000;
          const periodCorrectedRecommended = correctedBiomass * (row.sfr_percent / 100);
          totalCorrectedRecommendedKg += periodCorrectedRecommended;

          // ★ Period-by-period waste: capture actual overfeeding per day
          const periodActual = row.daily_feed_kg || 0;
          const periodWaste = Math.max(0, periodActual - periodCorrectedRecommended);
          totalWastedFeedKg += periodWaste;
        }
      }
    } else {
      // No adjustment — use reported recommended feed as-is, gated to feed days.
      totalCorrectedRecommendedKg = feedDays.reduce((sum, r) => sum + (r.recommended_feed_kg || 0), 0);

      // ★ Period-by-period waste using reported values (feed days only)
      for (const row of feedDays) {
        const periodActual = row.daily_feed_kg || 0;
        const periodRecommended = row.recommended_feed_kg || 0;
        const periodWaste = Math.max(0, periodActual - periodRecommended);
        totalWastedFeedKg += periodWaste;
      }
    }

    if (totalCorrectedRecommendedKg > 0) {
      rfi = Math.round((totalActualFeedKg / totalCorrectedRecommendedKg) * 1000) / 10;
      if (rfi > 100) feedingState = 'Overfeeding';
      else if (rfi >= 85) feedingState = 'Optimal';
      else feedingState = 'Restricted';
    }
  } else if (activeRecord?.percent_feeding_of_model != null) {
    rfi = activeRecord.percent_feeding_of_model;
    if (rfi > 100) feedingState = 'Overfeeding';
    else if (rfi >= 85) feedingState = 'Optimal';
    else feedingState = 'Restricted';
  }

  // ★ Wasted feed — sum of period-by-period positive deviations (overfeeding only)
  // This captures TRUE operational waste, regardless of cumulative RFI classification.
  // Round to 2 decimal places for display.
  const wastedFeedKg = Math.round(totalWastedFeedKg * 100) / 100;
  
  // Legacy excessFeedKg — cumulative difference (kept for backward compatibility / comparative display)
  // This is the "net" excess after late-cycle underfeeding offsets early overfeeding.
  const excessFeedKg = (rfi != null && rfi > 100)
    ? Math.round((totalActualFeedKg - totalCorrectedRecommendedKg) * 100) / 100
    : 0;

  // ── 6e. Recent Period RFI (last 14 days) — catches mixed feeding scenarios ─
  //    Cumulative RFI can mask recent overfeeding after a period of underfeeding.
  //    This rolling window surfaces late-cycle overfeeding that cumulative averaging hides.
  let recentRfi = null;
  let recentFeedingState = null;
  const RECENT_WINDOW = 14;
  if (daily_analysis_rows && daily_analysis_rows.length > RECENT_WINDOW) {
    const recentRows = daily_analysis_rows.slice(-RECENT_WINDOW);
    // Recent RFI — same symmetric gate: feed days only on both sides of the ratio.
    const recentFeedDays = recentRows.filter(r => (r.daily_feed_kg || 0) > 0);
    const recentActual = recentFeedDays.reduce((sum, r) => sum + (r.daily_feed_kg || 0), 0);
    let recentCorrectedRecommended = 0;

    const useAdjustedRecent = initialAssumedFishNumber && initialAssumedFishNumber !== originalFishNumber;
    if (useAdjustedRecent) {
      // Compute cumulative mortality up to the start of the recent window
      const priorRows = daily_analysis_rows.slice(0, -RECENT_WINDOW);
      let cumulativeMortalityPrior = priorRows.reduce((sum, r) => sum + (r.daily_mortality || 0), 0);
      let cumulativeHarvestPrior = priorRows.reduce((sum, r) => sum + (r.harvest_number || 0), 0);

      let runningMortality = cumulativeMortalityPrior;
      let runningHarvest = cumulativeHarvestPrior;
      for (const row of recentRows) {
        // Mortality / harvest still accrue daily.
        runningMortality += row.daily_mortality || 0;
        runningHarvest += row.harvest_number || 0;
        let correctedFishCount = Math.max(0, initialAssumedFishNumber - runningMortality - runningHarvest);
        // Recommended only on feed days.
        if ((row.daily_feed_kg || 0) > 0
            && row.actual_weight_g != null
            && correctedFishCount > 0
            && row.sfr_percent != null) {
          const correctedBiomass = (row.actual_weight_g * correctedFishCount) / 1000;
          recentCorrectedRecommended += correctedBiomass * (row.sfr_percent / 100);
        }
      }
    } else {
      recentCorrectedRecommended = recentFeedDays.reduce((sum, r) => sum + (r.recommended_feed_kg || 0), 0);
    }

    if (recentCorrectedRecommended > 0) {
      recentRfi = Math.round((recentActual / recentCorrectedRecommended) * 1000) / 10;
      if (recentRfi > 100) recentFeedingState = 'Overfeeding';
      else if (recentRfi >= 85) recentFeedingState = 'Optimal';
      else recentFeedingState = 'Restricted';
    }
  }

  // ── 7. Economic impact (Spreadsheet mode) ─────────────────────────────
  let economicImpact = null;
  if (isSpreadsheetMode && detectionResults) {
    const ecoParams = {
      average_feed_price_eur_per_kg: parseFloat(economicParams.average_feed_price_eur_per_kg) || 1.50,
      fish_price_eur_per_kg: economicParams.fish_price_eur_per_kg !== '' && economicParams.fish_price_eur_per_kg != null
        ? parseFloat(economicParams.fish_price_eur_per_kg) : null,
      target_harvest_weight_kg: economicParams.target_harvest_weight_kg !== '' && economicParams.target_harvest_weight_kg != null
        ? parseFloat(economicParams.target_harvest_weight_kg) : null,
    };
    economicImpact = calculateEconomicImpact(detectionResults, ecoParams);
  }

  // ── 8. Data quality (Spreadsheet mode) ────────────────────────────────
  let dataQuality = null;
  let sampleAnomalies = [];
  if (isSpreadsheetMode && activeRecord) {
    dataQuality = calculateDataQualityScore(
      activeRecord.data_points,
      activeRecord.sample_points,
      activeRecord.days_in_water || (activeRecord.data_points || []).length,
    );
    
    // Run anomaly detection on samples and apply confidence penalty
    const samplePts = (activeRecord.sample_points || []).map((s, idx) => ({
      weight_g: parseFloat(s.weight) || 0,
      day: s.day_number || idx * 30,
      date: s.date,
    }));
    sampleAnomalies = detectSampleAnomalies(samplePts, activeRecord.species || 'Seabream');
    if (sampleAnomalies.length > 0 && dataQuality) {
      const { adjustedScore, penalty, reason } = applyAnomalyPenalty(dataQuality.score, sampleAnomalies);
      dataQuality = {
        ...dataQuality,
        score: adjustedScore,
        anomalyPenalty: penalty,
        anomalyReason: reason,
        originalScore: dataQuality.score,
      };
    }
  }
  
  // ── 8b. Fish count resolution (unified priority order) ────────────────
  const fcrAlgebraicCount = fcrPanelMissingFish != null
    ? (originalFishNumber - fcrPanelMissingFish)
    : null;
  const jumpBasedCount = (coreIntelligence?.assumed_fish_number && coreIntelligence.assumed_fish_number < originalFishNumber)
    ? coreIntelligence.assumed_fish_number
    : null;
  const fishCountResolution = resolveFishCount({
    fcrAlgebraicCount,
    jumpBasedCount,
    reportedCount: originalFishNumber,
  });

  // ── 9. Mortality reality check ────────────────────────────────────────
  let mortalityCheck = null;
  if (isSpreadsheetMode && activeRecord) {
    mortalityCheck = checkMortalityReality(
      activeRecord.total_mortalities || 0,
      activeRecord.input_number || 0,
      coreIntelligence,
    );
  }

  // ── 10. Sampling recommendation ───────────────────────────────────────
  const samplingRecommendation = getSamplingRecommendation(coreIntelligence);

  // ── 11. Graph insight — deferred until after SSOT computation (section 15) ──
  //    Placeholder: computed below after ssot object is built.

  // ── 12. Shared Metrics — single source of truth for all report sections ─
  //    Computed ONCE here. No section may redefine or override these.
  //    Uses last ACTIVE comparison (respecting exclusions).
  const activeComparisons = comparisons.filter((_, idx) => !excludedIndices.has(idx));
  const lastComp = activeComparisons.length > 0
    ? activeComparisons[activeComparisons.length - 1]
    : comparisons[comparisons.length - 1];
  const smActual = parseFloat(lastComp.actual_weight_g);
  const smRawModel = parseFloat(lastComp.model_weight_g);
  const smModel = smRawModel * activeScaling;  // Use active baseline (adjusted if scaling applied)
  const smDeviationPercent = smModel > 0 ? ((smActual - smModel) / smModel) * 100 : 0;

  const smActualFCR = isSpreadsheetMode
    ? (reportedClosingFCR || null)
    : (fcrBasedDetection?.reportedBiologicalFCR || null);
  const smModelFCR = weightBasedModelFCR || (fcrBasedDetection?.modelBiologicalFCR || null);
  const smFcrGap = (smActualFCR != null && smModelFCR != null) ? smActualFCR - smModelFCR : null;

  const smGrowthDirection = smDeviationPercent > 5 ? 'above' : smDeviationPercent < -5 ? 'below' : 'within';
  const smMissingFishFlag = smFcrGap != null ? smFcrGap <= -0.1 : false;

  const sharedMetrics = {
    deviation_percent: Math.round(smDeviationPercent * 100) / 100,
    actual_weight: smActual,
    model_weight: smModel,
    fcr_gap: smFcrGap != null ? Math.round(smFcrGap * 1000) / 1000 : null,
    actual_fcr: smActualFCR,
    model_fcr: smModelFCR,
    growth_direction: smGrowthDirection,
    missing_fish_flag: smMissingFishFlag,
    sample_date: lastComp.sample_date || null,
    days_since_arrival: lastComp.days_since_arrival || null,
  };

  // ── 13. Override explanations with sharedMetrics (single source of truth) ─
  //    Explanations must align strictly with the active detection method AND
  //    the overall trend classification (not just the last sample direction).
  //    When jump is not active, never reference: confirmed upward jump,
  //    sudden weight increase, or corroboration by jump analysis.
  //    When unconfirmed jumps exist, explicitly name the erroneous sample.
  // ★ PURGED: All explanations generation deleted — narrative from ssotOverride.js only
  coreIntelligence.explanations = [];

  // ── 14. Detection Confidence — strength of the active detection signal ──
  //    Separate from data quality (sampling reliability). Based on FCR gap
  //    magnitude, jump credibility, and cross-method corroboration.
  //    Poor sampling frequency does NOT reduce detection confidence when FCR
  //    gives a clear signal.
  let detConfScore = 0;
  const detConfDrivers = [];

  if (activeDetectionMethod === 'fcr' && fcrHasSignal) {
    const absFcrGap = smFcrGap != null ? Math.abs(smFcrGap) : 0;
    if (absFcrGap >= 0.5) {
      detConfScore += 45;
      detConfDrivers.push(`Strong FCR deviation (${smFcrGap.toFixed(2)})`);
    } else if (absFcrGap >= 0.3) {
      detConfScore += 35;
      detConfDrivers.push(`Moderate FCR deviation (${smFcrGap.toFixed(2)})`);
    } else if (absFcrGap >= 0.1) {
      detConfScore += 20;
      detConfDrivers.push(`Mild FCR deviation (${smFcrGap.toFixed(2)})`);
    }
    if (fcrFishNumber && originalFishNumber) {
      const fcrMissingPct = ((originalFishNumber - fcrFishNumber) / originalFishNumber) * 100;
      if (fcrMissingPct >= 10) {
        detConfScore += 30;
        detConfDrivers.push(`FCR indicates ~${fcrMissingPct.toFixed(0)}% fish missing`);
      } else if (fcrMissingPct >= 5) {
        detConfScore += 20;
        detConfDrivers.push(`FCR indicates ~${fcrMissingPct.toFixed(0)}% fish missing`);
      }
    }
    // Cross-method corroboration: only when jump is genuinely active
    if (jumpHasSignal && jumpIsAboveModel) {
      detConfScore += 20;
      detConfDrivers.push('Supported by sample jump analysis');
    }
  } else if (activeDetectionMethod === 'jump' && jumpHasSignal) {
    const jConf = trajectoryJump.trajectory_jump_confidence || 'Low';
    if (jConf === 'High') { detConfScore += 50; detConfDrivers.push('High-confidence sample jump confirmed'); }
    else if (jConf === 'Moderate') { detConfScore += 35; detConfDrivers.push('Sample jump detected (jump method: moderate)'); }
    else { detConfScore += 20; detConfDrivers.push('Weak sample jump signal detected'); }
    if (fcrHasSignal) {
      detConfScore += 25;
      detConfDrivers.push('Corroborated by FCR analysis');
    }
  }

  // Issue 3: When FCR indicates strong missing fish, floor at Moderate
  if (fcrHasSignal && smFcrGap != null && Math.abs(smFcrGap) >= 0.2) {
    detConfScore = Math.max(detConfScore, 40);
    if (!detConfDrivers.some(d => d.includes('FCR'))) {
      detConfDrivers.push('Strong FCR signal ensures at least Moderate confidence');
    }
  }

  detConfScore = Math.min(detConfScore, 100);
  const detConfLabel = detConfScore >= 70 ? 'High' : detConfScore >= 40 ? 'Moderate' : 'Low';

  // Merge data quality improvement drivers from core intelligence (sample exclusion boost)
  const exclusionDrivers = (coreIntelligence.confidence?.drivers || []).filter(
    d => d.includes('Data quality improved')
  );

  const detectionConfidence = {
    score: detConfScore,
    label: detConfLabel,
    drivers: [...detConfDrivers, ...exclusionDrivers],
  };

  // ── 15. Single Source of Truth (SSOT) ────────────────────────────────
  //    Canonical values for Detection Strength, Primary Detection Method,
  //    and Adjusted Fish Count. NO component may derive its own versions.
  //    Every UI section must read from this object.
  const ssotMissingCount = (originalFishNumber && initialAssumedFishNumber && initialAssumedFishNumber < originalFishNumber)
    ? originalFishNumber - initialAssumedFishNumber
    : 0;

  // ══════════════════════════════════════════════════════════════════════════
  // FINANCIAL CONFIDENCE LEVEL — Single source of truth for all financial displays
  // ══════════════════════════════════════════════════════════════════════════
  // Levels:
  //   'confirmed'      — High reliability (≥70), no conflicts, no sample errors. Show: "Impact: €X"
  //   'indicative'     — Moderate reliability (50-69) OR minor downgrades. Show: "Indicative impact: €X (unconfirmed)"
  //   'not_actionable' — Low reliability (<50) OR severe downgrades OR no feed data. Show: "Insufficient data for financial estimate"
  //
  // This determines how ALL sections display financial figures. No component may derive its own level.
  
  const feedCostPerKgVal = parseFloat(economicParams?.average_feed_price_eur_per_kg) || 0;
  const hasFeedDataForFinancials = feedCostPerKgVal > 0;
  
  // ══════════════════════════════════════════════════════════════════════════
  // CONFLICT DETECTION — FCR vs Growth/Jump alignment check
  // ══════════════════════════════════════════════════════════════════════════
  // If FCR indicates missing fish but Jump/Growth does not confirm → CONFLICT
  const fcrIndicatesMissing = fcrBasedDetection?.detected && fcrBasedDetection?.estimatedMissingFish > 0;
  const jumpConfirmed = trajectoryJump?.jump_event_confirmed || trajectoryJump?.trajectory_jump_confirmed;
  const jumpDetectedButNotConfirmed = (trajectoryJump?.jump_event_detected || trajectoryJump?.trajectory_jump_detected) && !jumpConfirmed;
  
  // Conflict: FCR says missing fish, but jump is not confirmed or growth doesn't support
  const hasSignalConflict = fcrIndicatesMissing && (
    jumpDetectedButNotConfirmed ||
    (methodResolution?.jumpAvailable && !jumpConfirmed) ||
    (sharedMetrics?.growth_direction !== 'below' && !jumpConfirmed)
  );
  
  // Legacy conflict check (different estimates between methods)
  const hasConflictingMethods = !!(
    methodResolution?.jumpAvailable && 
    methodResolution?.fcrAvailable &&
    methodResolution?.jumpFishNumber !== methodResolution?.fcrFishNumber
  );
  
  // Combined conflict flag
  const hasAnyConflict = hasSignalConflict || hasConflictingMethods;
  
  const hasSuspectedError = dataQuality?.details?.suspectedSampleError || 
    (dataQuality?.details?.fcrVolatility && dataQuality.details.fcrVolatility > 0.3) ||
    // ★ Extreme-FCR outliers force suspected-error status so the unified decision
    //   state will cap at MONITOR_ONLY (or VALIDATION_REQUIRED via conflict gate).
    fcrOutliers.hasExtremeFCR;
  const hasHighVolatility = dataQuality?.details?.fcrVolatility && dataQuality.details.fcrVolatility > 0.25;
  const reliabilityScoreVal = dataQuality?.score ?? 50;
  
  // ══════════════════════════════════════════════════════════════════════════
  // PRECISION CONTROL — Round values when reliability is low
  // ══════════════════════════════════════════════════════════════════════════
  const needsPrecisionControl = reliabilityScoreVal < 80 || hasAnyConflict || hasSuspectedError;
  
  // Apply rounding: fish count → nearest 100, percentage → whole number
  const displayMissingFishCount = needsPrecisionControl 
    ? Math.round(ssotMissingCount / 100) * 100 
    : ssotMissingCount;
  const displayMissingFishPercent = ssotMissingCount > 0 && originalFishNumber
    ? (needsPrecisionControl 
        ? Math.round((ssotMissingCount / originalFishNumber) * 100) 
        : Math.round((ssotMissingCount / originalFishNumber) * 1000) / 10)
    : 0;
  
  let financialConfidenceLevel;
  let financialConfidenceReason;
  
  if (!hasFeedDataForFinancials) {
    financialConfidenceLevel = 'not_actionable';
    financialConfidenceReason = 'No feed cost data available';
  } else if (reliabilityScoreVal < 50) {
    financialConfidenceLevel = 'not_actionable';
    financialConfidenceReason = 'Data reliability too low for financial estimates';
  } else if (hasAnyConflict) {
    financialConfidenceLevel = 'indicative';
    financialConfidenceReason = 'Conflicting signals: FCR indicates potential stock discrepancy, but growth data does not confirm';
  } else if (hasSuspectedError) {
    financialConfidenceLevel = 'indicative';
    financialConfidenceReason = 'Suspected sample error detected';
  } else if (hasHighVolatility) {
    financialConfidenceLevel = 'indicative';
    financialConfidenceReason = 'High sample volatility';
  } else if (reliabilityScoreVal >= 70) {
    financialConfidenceLevel = 'confirmed';
    financialConfidenceReason = 'High data reliability with consistent signals';
  } else {
    financialConfidenceLevel = 'indicative';
    financialConfidenceReason = 'Moderate data reliability';
  }
  
  // Calculate canonical financial values
  const financialWastedFeedKg = wastedFeedKg > 0 ? wastedFeedKg : 0;
  const financialImpactEur = financialWastedFeedKg > 0 && hasFeedDataForFinancials
    ? Math.round(financialWastedFeedKg * feedCostPerKgVal * 100) / 100
    : 0;

  // ══════════════════════════════════════════════════════════════════════════
  // CANNIBALISM RISK INDICATOR — Track days with high-risk conditions
  // ══════════════════════════════════════════════════════════════════════════
  // When RFI falls below 50% at temperatures above 21°C and fish are under 100g,
  // this is a potential indicator of fish loss due to cannibalism in juvenile fish.
  // Track and display the number of days this condition occurs.
  //
  // Species scope: the cannibalism mechanism is biologically meaningful for
  // Seabream (and Tripletail). It does NOT apply to Tilapia or European
  // Seabass at the sizes covered by the platform's BFCR range, so we skip
  // the detector entirely for those species.
  
  const cannibalismSpecies = (
    isSpreadsheetMode
      ? activeRecord?.species
      : growthModelResult?.species
  ) || '';
  const _csl = cannibalismSpecies.toLowerCase();
  const cannibalismApplies = !(
    _csl.includes('tilapia') || _csl.includes('oreochromis') ||
    _csl.includes('seabass') || _csl.includes('dicentrarchus')
  );
  let cannibalismRiskDays = 0;
  let cannibalismRiskPeriods = [];
  
  if (cannibalismApplies && daily_analysis_rows && daily_analysis_rows.length > 0) {
    for (const row of daily_analysis_rows) {
      const dailyRfi = row.recommended_feed_kg > 0 
        ? ((row.daily_feed_kg || 0) / row.recommended_feed_kg) * 100 
        : null;
      const temp = row.temperature_c || row.temperature || null;
      const weight = row.actual_weight_g || row.weight_g || null;
      
      // Check cannibalism risk conditions:
      // - RFI < 50%
      // - Temperature > 21°C
      // - Fish weight < 100g
      if (dailyRfi !== null && dailyRfi < 50 && 
          temp !== null && temp > 21 && 
          weight !== null && weight < 100) {
        cannibalismRiskDays++;
        cannibalismRiskPeriods.push({
          date: row.date,
          rfi: Math.round(dailyRfi * 10) / 10,
          temperature: temp,
          weight: Math.round(weight * 10) / 10,
        });
      }
    }
  }
  
  const cannibalismRisk = !cannibalismApplies ? {
    detected: false,
    notApplicable: true,
    species: cannibalismSpecies || null,
    daysAtRisk: 0,
    riskLevel: 'N/A',
    message: `Cannibalism risk does not apply to ${cannibalismSpecies || 'this species'}.`,
    explanation: null,
    recommendation: null,
    periods: [],
  } : cannibalismRiskDays > 0 ? {
    detected: true,
    daysAtRisk: cannibalismRiskDays,
    riskLevel: cannibalismRiskDays >= 14 ? 'High' : cannibalismRiskDays >= 7 ? 'Moderate' : 'Low',
    message: `${cannibalismRiskDays} day${cannibalismRiskDays !== 1 ? 's' : ''} with cannibalism risk conditions detected`,
    explanation: 'Conditions associated with elevated cannibalism risk detected (low feeding RFI <50%, warm water >21°C, small fish <100g). The platform is flagging environmental risk factors only — no diagnostic causality is implied.',
    recommendation: cannibalismRiskDays >= 7 
      ? 'Conditions associated with elevated cannibalism risk detected. Consider increasing feeding frequency or reviewing stocking density to reduce risk exposure.'
      : 'Monitor for behavioural signs (aggression, size spread). Ensure adequate feed distribution.',
    periods: cannibalismRiskPeriods.slice(0, 10), // Show first 10 for reference
  } : {
    detected: false,
    daysAtRisk: 0,
    riskLevel: 'None',
    message: 'No cannibalism risk conditions detected',
    explanation: null,
    recommendation: null,
    periods: [],
  };

  // ══════════════════════════════════════════════════════════════════════════
  // METHOD CONFLICT COMPARISON — Detailed comparison when FCR vs Jump differ
  // ══════════════════════════════════════════════════════════════════════════
  // When both methods are available but show significantly different estimates,
  // provide a detailed comparison to help users understand the discrepancy.
  
  const fcrMissingEstimate = fcrBasedDetection?.estimatedMissingFish || 0;
  const jumpMissingEstimate = trajectoryJump?.jump_event_confirmed 
    ? (originalFishNumber - (trajectoryJump?.best_fit_fish_number || originalFishNumber))
    : 0;
  
  const methodConflictPercentDiff = fcrMissingEstimate > 0 && jumpMissingEstimate > 0
    ? Math.abs(((fcrMissingEstimate - jumpMissingEstimate) / Math.max(fcrMissingEstimate, jumpMissingEstimate)) * 100)
    : 0;
  
  const hasSignificantMethodConflict = methodConflictPercentDiff > 30 && fcrMissingEstimate > 0 && jumpMissingEstimate > 0;
  
  const methodConflictComparison = {
    hasConflict: hasSignificantMethodConflict || (fcrMissingEstimate > 0 && jumpMissingEstimate === 0) || (fcrMissingEstimate === 0 && jumpMissingEstimate > 0),
    fcrEstimate: fcrMissingEstimate,
    jumpEstimate: jumpMissingEstimate,
    percentDifference: Math.round(methodConflictPercentDiff),
    fcrHasFeedData: !!fcrBasedDetection?.detected,
    jumpHasConfirmedSignal: !!((trajectoryJump?.jump_event_confirmed || trajectoryJump?.trajectory_jump_confirmed)
      && trajectoryJump?.estimation_quality !== 'poor'
      && !(trajectoryJump?.gap_days > 120)),
    primaryMethod: activeDetectionMethod,
    recommendedEstimate: activeDetectionMethod === 'fcr' ? fcrMissingEstimate : jumpMissingEstimate,
    // Explanation of why estimates differ
    conflictExplanation: hasSignificantMethodConflict
      ? `FCR Analysis estimates ${fcrMissingEstimate.toLocaleString()} missing fish while Jump Analysis estimates ${jumpMissingEstimate.toLocaleString()} (${Math.round(methodConflictPercentDiff)}% difference). This discrepancy may be due to different data inputs: FCR uses daily feed records while Jump uses weight sample trajectories.`
      : fcrMissingEstimate > 0 && jumpMissingEstimate === 0
        ? `FCR Analysis indicates ${fcrMissingEstimate.toLocaleString()} missing fish, but Jump Analysis shows no confirmed signal. FCR has complete feed data and is more reliable in this case.`
        : fcrMissingEstimate === 0 && jumpMissingEstimate > 0
          ? `Jump Analysis indicates ${jumpMissingEstimate.toLocaleString()} missing fish, but FCR Analysis is unavailable (missing feed data). Use Jump estimate with caution.`
          : null,
    // Which method to trust
    reliabilityNote: fcrBasedDetection?.detected && !trajectoryJump?.jump_event_confirmed
      ? 'FCR Analysis (with feed data) is generally more reliable than Jump Analysis for estimating missing fish.'
      : trajectoryJump?.jump_event_confirmed && !fcrBasedDetection?.detected
        ? 'Only Jump Analysis available — consider collecting feed data for more accurate estimates.'
        : hasSignificantMethodConflict
          ? 'Both methods available but show different estimates. FCR Analysis is preferred when feed data is complete.'
          : 'Methods are in agreement.',
  };

  // ══════════════════════════════════════════════════════════════════════════
  // FEEDING CLASSIFICATION — Reconciled from Corrected RFI + period-level variance
  // ══════════════════════════════════════════════════════════════════════════
  // Single source of truth for feeding state. Economic significance of period
  // waste is derived from waste share vs total actual feed. Downstream consumers
  // (Summary, Economic Impact, Wingman nudge/insight) MUST align with this.
  //
  //   RFI > 100                        → Overfeeding (dominant)
  //   RFI 85–100  & waste > 0          → Mixed (tagged significant if share ≥ 5%)
  //   RFI 85–100  & waste = 0          → Optimal
  //   RFI < 85    & waste > 0          → Mixed (restricted overall, periodic overfeeding)
  //   RFI < 85    & waste = 0          → Restricted
  //
  // ECONOMIC-SIGNIFICANCE THRESHOLD: wasteShare ≥ 5% of total actual feed
  //   (captures both cost materiality and operational drift; tuned to avoid
  //   flagging trivial day-to-day noise while catching real overfeeding drift).
  const wasteShare = totalActualFeedKg > 0
    ? (wastedFeedKg / totalActualFeedKg)
    : 0;
  const isWasteEconomicallySignificant = wasteShare >= 0.05;
  const wasteSharePct = Math.round(wasteShare * 1000) / 10; // 1-decimal %

  let correctedFeedingState = feedingState;
  let correctedFeedingNote = null;

  if (rfi == null) {
    correctedFeedingState = null;
    correctedFeedingNote = null;
  } else if (rfi > 100) {
    correctedFeedingState = 'Overfeeding';
    correctedFeedingNote = `Dominant overfeeding: actual feed is ${Math.round(rfi - 100)}% above corrected recommendation (RFI ${rfi}%).`;
  } else if (rfi >= 85 && wastedFeedKg > 0) {
    correctedFeedingState = 'Mixed';
    correctedFeedingNote = isWasteEconomicallySignificant
      ? `Mixed feeding pattern: cumulative RFI ${rfi}% is within the optimal band, but ${wastedFeedKg.toLocaleString()} kg of period-level overfeeding was detected (~${wasteSharePct}% of total feed) — economically significant.`
      : `Mixed feeding pattern: broadly aligned with model (RFI ${rfi}%) with minor period-level variance (${wastedFeedKg.toLocaleString()} kg waste, ~${wasteSharePct}% of total feed — not economically significant).`;
  } else if (rfi >= 85) {
    correctedFeedingState = 'Optimal';
    correctedFeedingNote = null;
  } else if (wastedFeedKg > 0) {
    // RFI < 85 but some periods overfed
    correctedFeedingState = 'Mixed';
    correctedFeedingNote = isWasteEconomicallySignificant
      ? `Mixed feeding pattern: restricted overall (RFI ${rfi}%) with periodic overfeeding of ${wastedFeedKg.toLocaleString()} kg (~${wasteSharePct}% of total feed) — economically significant.`
      : `Mixed feeding pattern: restricted overall (RFI ${rfi}%) with minor periodic overfeeding (${wastedFeedKg.toLocaleString()} kg, ~${wasteSharePct}% of total feed).`;
  } else {
    correctedFeedingState = 'Restricted';
    correctedFeedingNote = `Restricted feeding: actual feed is ${Math.round(100 - rfi)}% below corrected recommendation (RFI ${rfi}%).`;
  }

  // ══════════════════════════════════════════════════════════════════════════
  // JUMP / GROWTH ALIGNMENT — Standardized wording for unconfirmed jumps
  // ══════════════════════════════════════════════════════════════════════════
  let jumpStatusMessage = null;
  if (jumpDetectedButNotConfirmed) {
    jumpStatusMessage = 'No sustained upward shift — detected jumps are not confirmed';
  } else if (trajectoryJump?.jump_event_detected && jumpConfirmed) {
    // Quality- AND reliability-aware language: never put "confirmed"
    // alongside "insufficient confirmation" wording in the same report.
    // - Poor data quality:                    indicative-only (no "confirmed").
    // - Confirmed jump + low reliability:     "Trajectory jump confirmed —
    //                                          operational confidence remains
    //                                          low". This phrasing matches the
    //                                          PRD wording fix (#2) and avoids
    //                                          the contradiction with the
    //                                          reliability badge.
    // - Confirmed jump + healthy reliability: standard confirmed wording.
    const isLowQuality = trajectoryJump.estimation_quality === 'poor' || (trajectoryJump.gap_days && trajectoryJump.gap_days > 120);
    if (isLowQuality) {
      jumpStatusMessage = 'Jump pattern detected but data too sparse for confirmation — treat as indicative signal only';
    } else if (reliabilityScoreVal != null && reliabilityScoreVal < 50) {
      jumpStatusMessage = 'Trajectory jump confirmed — operational confidence remains low';
    } else {
      jumpStatusMessage = 'Jump detected and confirmed by subsequent samples';
    }
  }

  // ══════════════════════════════════════════════════════════════════════════
  // UNIFIED CONCLUSION — Single top-level statement that all sections must align with
  // ══════════════════════════════════════════════════════════════════════════
  // This is the ONE conclusion displayed at the top of any report.
  // All downstream sections must support this conclusion, not contradict it.
  // 
  // ★ CRITICAL: No section may independently declare "missing fish confirmed" 
  //   or "no missing fish" — they MUST use these canonical messages.
  
  let conclusionType; // 'missing_fish' | 'growth_suppression' | 'extra_fish' | 'normal' | 'conflict'
  let conclusionStatement;
  let conclusionConfidenceLabel;
  let conclusionHasConflict = hasAnyConflict;
  let conclusionConflictNote = null;
  let conclusionInterpretation = null;
  let conclusionDetectionExplanation = null;
  let conclusionGrowthInsight = null;
  
  // ★ CONFLICT RESOLUTION: FCR vs Growth standardized message
  const CONFLICT_MESSAGE = 'Conflicting signals: FCR indicates potential stock discrepancy, but growth data does not confirm. Confidence reduced — validation required.';
  const JUMP_NOT_CONFIRMED_MESSAGE = 'No sustained upward shift — detected jumps are not confirmed';
  
  if (hasAnyConflict && ssotMissingCount > 0) {
    // CONFLICT CASE: FCR indicates missing fish but growth/jump doesn't confirm
    conclusionType = 'conflict';
    conclusionConfidenceLabel = 'Reduced';
    conclusionStatement = CONFLICT_MESSAGE;
    conclusionConflictNote = CONFLICT_MESSAGE;
    conclusionInterpretation = CONFLICT_MESSAGE;
    conclusionDetectionExplanation = activeDetectionMethod === 'fcr' 
      ? 'FCR-based signal is primary, but conflicts with growth data, reducing confidence'
      : 'Jump-based signal is primary, but not confirmed by subsequent samples';
    conclusionGrowthInsight = jumpDetectedButNotConfirmed ? JUMP_NOT_CONFIRMED_MESSAGE : null;
  } else if (ssotMissingCount > 0) {
    // NO CONFLICT: Missing fish indicated with consistent signals
    conclusionType = 'missing_fish';
    conclusionConfidenceLabel = detConfLabel === 'High' ? 'Strong' : detConfLabel === 'Moderate' ? 'Moderate' : 'Possible';
    // ★ SSOT — downgrade primary statement to a soft "pattern observed" under low confidence.
    //   Low confidence here maps to MONITOR_ONLY at the downstream decisionContext layer.
    const isLowConfOrMonitor = detConfLabel === 'Low' || (reliabilityScoreVal != null && reliabilityScoreVal < 50);
    if (isLowConfOrMonitor) {
      // PRD wording-fix #2: when a jump is technically confirmed but
      // reliability is low, NEVER pair the badge "Confirmed" with the body
      // text "insufficient confirmation" — that's a direct contradiction
      // for the reader. Use the calibrated "operational confidence" wording
      // instead. The branch below is only entered if at least one of
      // jump/FCR/indicative signal exists, so the distinction is real.
      if (jumpConfirmed) {
        conclusionStatement = 'Trajectory jump confirmed — operational confidence remains low. A deviation pattern is observed that may indicate stock discrepancy; validate with additional sampling before acting.';
      } else {
        conclusionStatement = 'Indicative signal detected — estimate withheld due to insufficient confirmation. A deviation pattern is observed that may indicate stock discrepancy; validate with additional sampling before acting.';
      }
      conclusionInterpretation = 'A deviation pattern is observed that may indicate stock discrepancy.';
    } else {
      conclusionStatement = needsPrecisionControl
        ? `${conclusionConfidenceLabel} indication of biomass deviation: approximately ${displayMissingFishCount.toLocaleString()} fish (~${displayMissingFishPercent}%) unaccounted for.`
        : `${conclusionConfidenceLabel} indication of biomass deviation: approximately ${ssotMissingCount.toLocaleString()} fish (${Math.round(ssotMissingCount / originalFishNumber * 1000) / 10}%) unaccounted for.`;
      // Soften "definitive" wording even under Moderate/High — avoid "likely consuming feed" phrasing
      conclusionInterpretation = detConfLabel === 'High'
        ? 'Observed pattern is consistent with fewer fish than assumed.'
        : 'A deviation pattern is observed that may indicate stock discrepancy.';
    }
    conclusionDetectionExplanation = `${activeDetectionMethod === 'fcr' ? 'FCR' : 'Jump'}-based detection with ${detConfLabel.toLowerCase()} confidence`;
  } else if (sharedMetrics?.growth_direction === 'below') {
    conclusionType = 'growth_suppression';
    conclusionConfidenceLabel = 'Moderate';
    // PRD wording-fix #1 (Growth vs Model): never say "No missing fish
    // signal detected" or "Stock tracking normal" while ANY other
    // indicative signal exists in the same report — that's a direct
    // contradiction the customer will notice immediately. The check below
    // covers jump (detected OR confirmed OR pending), FCR conflict,
    // low-confidence indicative SSOT mode, and the downstream MONITOR_ONLY
    // / VALIDATION_REQUIRED decisionClass.
    const anyIndicativeSignal = !!(
      trajectoryJump?.jump_event_detected ||
      trajectoryJump?.trajectory_jump_detected ||
      trajectoryJump?.jump_confirmation_pending ||
      jumpConfirmed ||
      hasAnyConflict ||
      (reliabilityScoreVal != null && reliabilityScoreVal < 50) ||
      detConfLabel === 'Low'
    );
    conclusionStatement = anyIndicativeSignal
      ? 'No confirmed missing fish signal detected. Growth is currently tracking close to the adjusted model, but low-confidence deviation signals remain under review.'
      : 'Growth is tracking below model expectations. No missing fish signal detected — environmental or feeding factors may be limiting growth.';
    conclusionInterpretation = anyIndicativeSignal
      ? 'Growth is tracking close to the adjusted model. Low-confidence deviation signals remain under review and require additional sampling to confirm.'
      : 'Observed growth is tracking below model predictions. Environmental factors or feeding strategy may be limiting growth.';
    conclusionDetectionExplanation = anyIndicativeSignal
      ? 'No confirmed stock discrepancy detected — indicative signals remain under review'
      : 'No significant stock discrepancy detected';
  } else if (ssotMissingCount < 0) {
    conclusionType = 'extra_fish';
    conclusionConfidenceLabel = 'Low';
    conclusionStatement = 'Apparent extra fish in cage — likely a data entry or counting error.';
    conclusionInterpretation = 'Reported fish count may be lower than actual. Verify stock records.';
    conclusionDetectionExplanation = 'Anomaly in stock records detected';
  } else {
    conclusionType = 'normal';
    conclusionConfidenceLabel = 'N/A';
    // Same indicative-signal guard for the "Normal" branch — covers cases
    // where the missing-fish count rounds to zero / within margin but the
    // jump/FCR/low-reliability machinery is still flagging something.
    const anyIndicativeSignal = !!(
      trajectoryJump?.jump_event_detected ||
      trajectoryJump?.trajectory_jump_detected ||
      trajectoryJump?.jump_confirmation_pending ||
      jumpConfirmed ||
      hasAnyConflict ||
      (reliabilityScoreVal != null && reliabilityScoreVal < 50) ||
      detConfLabel === 'Low'
    );
    conclusionStatement = anyIndicativeSignal
      ? 'No confirmed missing fish signal detected. Growth is currently tracking close to the adjusted model, but low-confidence deviation signals remain under review.'
      : 'Stock integrity appears normal. Growth trajectory is within expected parameters.';
    conclusionInterpretation = anyIndicativeSignal
      ? 'Growth is tracking close to the adjusted model. Low-confidence deviation signals remain under review and require additional sampling to confirm.'
      : 'Growth trajectory is within expected parameters.';
    conclusionDetectionExplanation = anyIndicativeSignal
      ? 'No confirmed stock discrepancy detected — indicative signals remain under review'
      : 'No stock discrepancy detected';
  }
  
  // Build unified conclusion object — ALL sections MUST read from this
  // ★ SSOT — the decision class is sourced from the SAME resolveDecisionState()
  //   function that buildDecisionContext() uses. No second classifier allowed.
  const unifiedDecision = resolveDecisionState({
    reliabilityScore: reliabilityScoreVal,
    detectionStrength: detConfLabel,
    hasConflict: hasAnyConflict,
    hasSuspectedError,
    highVolatility: !!hasHighVolatility,
    hasFeedData: hasFeedDataForFinancials,
    sampleCount: dataQuality?.details?.totalSamples || dataQuality?.details?.sampleCount || 0,
    isSnapshot: !isSpreadsheetMode && !isGrowthModelMode,
    hasMissingFish: ssotMissingCount > 0,
  });
  const actionabilityLevel = unifiedDecision.decisionClass;

  const conclusion = {
    type: conclusionType,
    statement: conclusionStatement,
    confidenceLabel: conclusionConfidenceLabel,
    hasConflict: conclusionHasConflict,
    conflictNote: conclusionConflictNote,
    // ★ CANONICAL MESSAGES — use these exact strings in ALL sections
    interpretation: conclusionInterpretation,
    detectionExplanation: conclusionDetectionExplanation,
    growthInsight: conclusionGrowthInsight || (jumpDetectedButNotConfirmed ? JUMP_NOT_CONFIRMED_MESSAGE : null),
    jumpStatusMessage,
    // Detection summary (with precision control applied)
    missingFishCount: displayMissingFishCount,
    missingFishPercent: displayMissingFishPercent,
    rawMissingFishCount: ssotMissingCount,  // Original unrounded value
    rawMissingFishPercent: ssotMissingCount > 0 && originalFishNumber 
      ? Math.round((ssotMissingCount / originalFishNumber) * 1000) / 10 : 0,
    precisionControlApplied: needsPrecisionControl,
    detectionStrength: detConfLabel,
    primaryMethod: activeDetectionMethod,
    // Financial summary (aligned with financialConfidenceLevel)
    financialConfidenceLevel,
    financialConfidenceReason,
    wastedFeedKg: financialWastedFeedKg,
    financialImpactEur,
    feedCostPerKg: feedCostPerKgVal,
    // Formatted financial display values
    financialDisplayValue: financialConfidenceLevel === 'not_actionable'
      ? null
      : financialImpactEur,
    financialDisplayLabel: financialConfidenceLevel === 'confirmed'
      ? `Impact: €${financialImpactEur.toLocaleString()}`
      : financialConfidenceLevel === 'indicative'
        ? `Indicative impact: €${financialImpactEur.toLocaleString()} (unconfirmed)`
        : 'Insufficient data for financial estimate',
    // Data reliability
    dataReliabilityScore: reliabilityScoreVal,
    // ★ NAMESPACE: this is a DATA-RELIABILITY grade, NOT the confidence label.
    //   Kept on the conclusion object for internal use but surfaces MUST render
    //   the canonical confidence vocabulary (High/Moderate/Low/Indication Only/Uncertain).
    dataReliabilityLabel: reliabilityScoreVal >= 70 ? 'Good' : reliabilityScoreVal >= 50 ? 'Acceptable' : 'Low',
    // ★ CANONICAL CONFIDENCE LABEL — the ONLY label allowed on on-screen confidence badges.
    confidenceLabel: toCanonicalConfidenceLabel(detConfLabel, reliabilityScoreVal, {
      hasConflict: hasAnyConflict,
      isSingleSnapshot: !isSpreadsheetMode && !isGrowthModelMode,
    }),
    // Actionability
    actionability: actionabilityLevel,
    // ★ FEEDING CLASSIFICATION — corrected to avoid "Optimal" + waste coexistence
    feedingState: correctedFeedingState,
    feedingNote: correctedFeedingNote,
  };

  const ssot = {
    detectionStrength: detConfLabel,                         // 'High' | 'Moderate' | 'Low'
    detectionStrengthScore: detConfScore,                    // 0-100
    detectionStrengthDrivers: detConfDrivers,                // Array of explanation strings
    // ★ CANONICAL CONFIDENCE LABEL — one of the five PRD-allowed values.
    //   The ONLY label allowed on user-facing confidence badges.
    confidenceLabel: toCanonicalConfidenceLabel(detConfLabel, reliabilityScoreVal, {
      hasConflict: hasAnyConflict,
      isSingleSnapshot: !isSpreadsheetMode && !isGrowthModelMode,
    }),
    actionability: actionabilityLevel,                       // ★ 'ACTIONABLE' | 'VALIDATION_REQUIRED' | 'MONITOR_ONLY' — gates strong-claim language
    primaryMethod: activeDetectionMethod,                    // 'fcr' | 'jump'
    primaryMethodLabel: activeDetectionMethod === 'jump' ? 'Jump-Based' : 'FCR-Based',
    adjustedFishCount: initialAssumedFishNumber,
    originalFishCount: originalFishNumber,
    missingFishCount: displayMissingFishCount,               // ★ Precision-controlled (rounded when reliability < 80)
    missingFishPercent: displayMissingFishPercent,           // ★ Precision-controlled
    rawMissingFishCount: ssotMissingCount,                   // Original unrounded value
    rawMissingFishPercent: ssotMissingCount > 0 && originalFishNumber
      ? Math.round((ssotMissingCount / originalFishNumber) * 1000) / 10 : 0,
    // ★ MISSING-FISH RANGE — PRD canonical {lower, upper, midpoint}.
    //   All on-screen and PDF surfaces MUST use formatMissingFishRange(ssot).
    missingFishRange: computeMissingFishRange(ssotMissingCount, reliabilityScoreVal),
    missingFishRangeLabel: formatMissingFishRange({
      missingFishCount: ssotMissingCount,
      dataReliabilityScore: reliabilityScoreVal,
    }).label,
    dataReliabilityScore: reliabilityScoreVal,
    precisionControlApplied: needsPrecisionControl,
    hasMissingFish: ssotMissingCount > 0,
    hasConflict: hasAnyConflict,                             // ★ Conflict flag for all sections
    // ★ FCR outliers — implausible per-period FCR summary (gates hasSuspectedError)
    fcrOutliers,
    // Feeding Impact Classification — ★ CORRECTED to avoid "Optimal" + waste coexistence
    rfi,                                                     // Corrected Relative Feed Index (%)
    feedingState: correctedFeedingState,                     // ★ May be 'Mixed' if waste detected with optimal RFI
    feedingNote: correctedFeedingNote,                       // ★ Explanation when feeding state is corrected
    excessFeedKg,                                            // Legacy: cumulative excess (net after offsetting)
    wastedFeedKg,                                            // ★ Period-by-period waste (true operational waste)
    wasteShare,                                              // ★ Ratio of wasted feed to total actual feed (0..1)
    wasteSharePct,                                           // ★ Same, in percent (1-decimal)
    isWasteEconomicallySignificant,                          // ★ True when wasteShare ≥ 5% — gates Wingman overfeeding alerts
    totalActualFeedKg: Math.round(totalActualFeedKg * 100) / 100,
    totalCorrectedRecommendedKg: Math.round(totalCorrectedRecommendedKg * 100) / 100,
    recentRfi,                                               // Last 14-day RFI (catches late-cycle overfeeding)
    recentFeedingState,                                      // Recent period feeding state
    // ★ Feeding impact with corrected logic
    feedingImpact: correctedFeedingNote || (wastedFeedKg > 0
      ? `Feed waste detected: ${wastedFeedKg.toLocaleString()} kg wasted across overfeeding periods`
      : ssotMissingCount > 0
        ? (rfi >= 85
            ? 'Feed allocation misaligned with actual biomass (no direct waste)'
            : rfi != null
              ? 'Restricted feeding — no feed waste, but biomass error distorts performance metrics'
              : null)
        : null),
    // Jump status message — ★ STANDARDIZED wording
    jumpStatusMessage,
    // ★ CANNIBALISM RISK INDICATOR
    cannibalismRisk,
    // ★ METHOD CONFLICT COMPARISON
    methodConflictComparison,
    // ★ FINANCIAL CONFIDENCE LEVEL — canonical source for all financial displays
    financialConfidenceLevel,                                // 'confirmed' | 'indicative' | 'not_actionable'
    financialConfidenceReason,
    financialImpactEur,                                      // Canonical EUR impact value
    feedCostPerKg: feedCostPerKgVal,
    // ★ UNIFIED CONCLUSION — single statement that all sections must align with
    conclusion,
  };

  // ── 17. Graph insight — uses SSOT conclusion for consistent messaging ──
  // ★ graphInsight now reads from SSOT conclusion, not local derivation
  const graphInsight = getGraphInsight(coreIntelligence, detectionResults, overfeedingRisk, ssot, null);

  // ── 18. DECISION CONTEXT — Structured decision support wrapper ────────────
  //    Converts all outputs into actionable decision support with risk control.
  //    This is a RESPONSE WRAPPER, not new calculations.
  //    ★ The unified decision is passed in so buildDecisionContext MUST NOT
  //      re-derive it. Guarantees decisionContext.decisionClass === ssot.actionability.
  const decisionContext = buildDecisionContext({
    ssot,
    dataQuality,
    sharedMetrics,
    detectionConfidence,
    methodResolution,
    fcrBasedDetection,
    trajectoryJump,
    economicParams,
    rfi,
    wastedFeedKg,
    feedingState,
    isSpreadsheetMode,
    isGrowthModelMode,
    unifiedDecision,  // ★ pre-resolved decision state — SSOT
  });

  // ══════════════════════════════════════════════════════════════════════════
  // RUNTIME INVARIANT — SSOT guard against regressions
  // ══════════════════════════════════════════════════════════════════════════
  // Non-blocking: if ssot.actionability and decisionContext.decisionClass ever
  // diverge, something has reintroduced a second classifier path. Emit a
  // console.warn with both values + a dataset identifier so it is caught in
  // development and staging before shipping.
  if (ssot.actionability !== decisionContext.decisionClass) {
    const datasetId = activeRecord?.id
      || activeRecord?.name
      || growthModelResult?.id
      || growthModelResult?.unit_name
      || 'unknown-dataset';
    // eslint-disable-next-line no-console
    console.warn(
      '[DecisionState SSOT invariant violated] ssot.actionability ≠ decisionContext.decisionClass',
      {
        dataset: datasetId,
        ssotActionability: ssot.actionability,
        decisionContextClass: decisionContext.decisionClass,
        detectionStrength: ssot.detectionStrength,
        reliabilityScore: reliabilityScoreVal,
        hasConflict: hasAnyConflict,
        hasSuspectedError,
      }
    );
  }

  // ── Return unified result ─────────────────────────────────────────────
  return {
    // Core deviation analysis (spread all fields from calculateMissingFishIntelligence)
    ...coreIntelligence,

    // Biomass detection results
    detectionResults,

    // Trajectory jump
    trajectoryJump,

    // Fish numbers
    originalFishNumber,
    initialAssumedFishNumber,

    // Detection method resolution
    methodResolution,

    // Supplementary analyses
    economicImpact,
    dataQuality,
    mortalityCheck,
    samplingRecommendation,
    overfeedingRisk,
    graphInsight,

    // Shared metrics — single source of truth for all sections
    sharedMetrics,

    // Detection confidence (signal strength, separate from data quality)
    detectionConfidence,

    // FCR-based missing fish detection
    fcrBasedDetection,

    // Growth-fit result
    growthFit,

    // ★ SSOT — canonical detection labels. All UI reads from here.
    ssot,

    // ★ DECISION CONTEXT — Structured decision support with risk control
    decisionContext,

    // ★ FISH COUNT RESOLUTION — unified priority order
    fishCountResolution,
    
    // ★ SAMPLE ANOMALIES — flagged suspicious data points
    sampleAnomalies,

    // Mode flags
    isSpreadsheetMode,
    isGrowthModelMode,
  };
};
